import React, { Component } from "react"
import PropTypes from "prop-types"
import isEqual from "lodash/isEqual"
import Highcharts from "highcharts/highstock"
import Exporting from "highcharts/modules/exporting"
import drilldown from "highcharts/modules/drilldown"
import { translate } from "react-i18next"
import { CHARTS } from "../../../constants"
import { getStackedDrilldownSeries, getStackedRootSeries } from "../../../utils/drilldown-utils"
import DrilldownShape from "../../../state/shapes/DrilldownShapes/DrilldownShape"

class DrilldownStackedChart extends Component {
  state = {
    chartDrilldownLevel: 0,
  }

  componentDidMount = () => {
    this.createChart()
  }

  shouldComponentUpdate = (nextProps) => {
    return (
      !isEqual(this.props.data, nextProps.data) ||
      !isEqual(this.props.yAxisTitle, nextProps.yAxisTitle)
    )
  }

  componentDidUpdate = () => {
    if (this.props.allowUpdate) {
      this.createChart()
    }
    if (this.props.onComponentUpdate) {
      this.props.onComponentUpdate()
    }
  }

  componentWillUnmount = () => {
    this.chart.destroy()
  }

  getStackLabelTotals = (series) => {
    const totals = []

    series.forEach((r) => {
      r.data.forEach((d) => {
        totals.push(d.count)
      })
    })

    return totals
  }

  createChart = () => {
    drilldown(Highcharts)

    // Get the element into which to insert the chart
    const chartContainer = this.chartContainerRef

    const {
      t,
      yAxisTitle,
      data,
      scrollbarEnabled,
      onDrillDown,
      onDrillUp,
      yAxisMaxValue,
      xAxisTitle,
      xAxisFormatter,
      titleKey,
      subtitleKey,
    } = this.props

    Exporting(Highcharts)

    const numberOfColumnsBeforeScroll = 15

    const seriesData = getStackedRootSeries(CHARTS, data.rootSeries, t)
    const drilldownData = getStackedDrilldownSeries(CHARTS, data.drilldownSeries, data.rootSeries)

    /** Updates the scrollbar and max count
     * Relies on HighStock as that's needed for scrollbars in Highcharts 💩
     * If the max is too high, it'll display empty spaces on the right hand side
     */
    const updateScrollAxis = (chart, columnCount, drillLevel) => {
      if (drillLevel === 0) {
        columnCount = Math.ceil(columnCount / 3)
      }
      // Column count wrong as it counts series
      const xAxisMax = scrollbarEnabled
        ? Math.min(numberOfColumnsBeforeScroll - 1, columnCount - 1)
        : undefined
      const enableScroll = scrollbarEnabled && columnCount > numberOfColumnsBeforeScroll

      chart.xAxis[0].update({
        max: xAxisMax,
        scrollbar: {
          enabled: enableScroll,
        },
      })
    }

    const getCategoryTranslationIdentifier = (category) => {
      switch (category) {
        case 0:
          return "Common.Potential.Low"
        case 1:
          return "Common.Potential.Medium"
        case 2:
          return "Common.Potential.High"
        default:
          break
      }
    }

    const onDrillUpEvent = (e, chart) => {
      const drillLevel = this.state.chartDrilldownLevel - 1
      if (drillLevel === e.seriesOptions._levelNumber) {
        if (e.seriesOptions._levelNumber != 0) {
          updateScrollAxis(chart, e.seriesOptions.data.length, drillLevel)
        } else {
          updateScrollAxis(chart, CalculateTotalSeries(), drillLevel)
        }

        if (onDrillUp) {
          onDrillUp(e, chart)
        }

        this.setState({ chartDrilldownLevel: drillLevel })
      }
    }

    const onDrillDownEvent = (e, chart) => {
      if (!e.seriesOptions) {
        const drilldownParent = e.point.drilldown
        if (drilldownData[drilldownParent]) {
          const series = drilldownData[drilldownParent]

          series.forEach((s) => {
            chart.addSingleSeriesAsDrilldown(e.point, s)
          })

          chart.applyDrilldown()

          const drillLevel = this.state.chartDrilldownLevel + 1

          // prettier-ignore-line
          updateScrollAxis(chart, series[0].data.length, drillLevel)
          if (onDrillDown) {
            onDrillDown(e, chart)
          }

          this.setState({ chartDrilldownLevel: drillLevel })
        }
      }
    }

    const CalculateTotalSeries = () => {
      console.log("seriesData", seriesData)
      let totalSeries = 0
      for (let i = 0 ; i < this.props.data.rootSeries.length ; i++)
      {
        let currentRoot = this.props.data.rootSeries[i]
        if (currentRoot.drilldownObjects.length > 0) {
          totalSeries += currentRoot.drilldownObjects.length
        }
        if (currentRoot.nonDrilldownObjects.length > 0) {
          totalSeries += currentRoot.nonDrilldownObjects.length
        }
      }
      return totalSeries;
    }

    this.chart = new Highcharts.Chart(
      {
        chart: {
          animation: false,
          renderTo: chartContainer,
          marginTop: 15,
          height: CHARTS.standardHeight - 35,
          type: "column",
          events: {
            drillup(e) {
              onDrillUpEvent(e, this)
            },
            drilldown(e) {
              onDrillDownEvent(e, this)
            },
          },
        },
        lang: {
          drillUpText: t("Back"),
          contextButtonTitle: t("Common.ChartDownloadOptions"),
        },
        title: {
          text: titleKey != null && titleKey.length > 0 ? t(titleKey) : null,
          y: -30,
        },
        subtitle: {
          text: subtitleKey != null && subtitleKey.length > 0 ? t(subtitleKey) : null,
          y: -15,
        },
        colors: CHARTS.colours,
        xAxis: {
          type: "category",
          labels: {
            formatter() {
              if (xAxisFormatter) {
                return xAxisFormatter(this.value)
              }
              return this.value
            },
            autoRotation: [-10, -20, -30, -40, -50, -60, -70, -80, -90],
          },

          titleKey: {
            text: xAxisTitle ? t(xAxisTitle) : null,
          },
          min: scrollbarEnabled ? 0 : undefined,
          max: numberOfColumnsBeforeScroll,
          scrollbar: {
            enabled: false,
          },
        },
        yAxis: {
          maxPadding: 0.1,
          allowDecimals: false,
          ceiling: yAxisMaxValue,
          max: yAxisMaxValue,
          title: {
            text: t(yAxisTitle),
          },
          stackLabels: {
            enabled: true,
            align: "center",
            crop: false,
            overflow: "allow",
            style: {
              fontWeight: "normal",
              fontSize: "10px",
            },
            formatter() {
              if (this.axis.chart.series[0].options.data[this.x]) {
                return this.axis.chart.series[0].options.data[this.x].employeeTotal
              }

              return ""
            },
          },
        },
        legend: {
          enabled: false,
        },
        credits: {
          enabled: false,
        },
        exporting: {
          enabled: CHARTS.exportEnabled,
          buttons: {
            contextButton: {
              menuItems: CHARTS.exportButtons,
            },
          },
          filename: titleKey != null && titleKey.length > 0 ? t(titleKey) : null,
          chartOptions: CHARTS.exportChartOptions,
        },
        plotOptions: {
          column: {
            stacking: "percentage",
            borderWidth: 0,
            cropThreshold: 2000,
            dataLabels: {
              enabled: true,
              color: "white",
              formatter() {
                if (this.y > 0) return this.y
                // if (this.y > 0) { return `${t(getCategoryTranslationIdentifier(this.point.stackCategory))} \r\n (${formatDecimalForDisplay(this.y)}%)` }
              },
            },
            minPointLength: 1, // This was 2 previously to account for auto-drill into root
          },
        },
        tooltip: {
          formatter() {
            return `<div><span style="font-size:11px"><b>${this.point.name}</b></span>
            <br>
            <span style="color:${this.point.color}">${t(getCategoryTranslationIdentifier(this.point.stackCategory))} </span>: 
            <b>${this.point.count}</b> people<br/>
          </div>`
          },
        },
        series: seriesData,
        drilldown: {
          animation: false,
          series: [],
          drillUpButton: {
            position: {
              x: 0,
              y: 25,
            },
          },
        },
      },
      (chart) => {
        // Chart rendered callback
        if (
          data.rootSeries &&
          data.rootSeries[0] &&
          (data.rootSeries[0].drilldownObjects || data.rootSeries[0].nonDrilldownObjects)
        ) {
          updateScrollAxis(chart, CalculateTotalSeries(), 0)
        }
      }
    )
  }

  render() {
    // the inline-block 99% width div fixes is a hacky fix
    // for an issue where the full width chart forces the whole
    // dashboard to overlap the left hand filter bar when on a
    // Microsoft Surface sized screen
    return (
      <div style={{ display: "inline-block", width: "99%", paddingTop: "5px" }}>
        <div
          className="chart-container"
          ref={(c) => {
            this.chartContainerRef = c
          }}
        />
      </div>
    )
  }
}

DrilldownStackedChart.propTypes = {
  data: PropTypes.shape(DrilldownShape).isRequired,
  yAxisTitle: PropTypes.string.isRequired,
  scrollbarEnabled: PropTypes.bool.isRequired,
  onDrillDown: PropTypes.func,
  onDrillUp: PropTypes.func,
  allowUpdate: PropTypes.bool,
  yAxisMaxValue: PropTypes.number,
  xAxisTitle: PropTypes.string.isRequired,
  xAxisFormatter: PropTypes.func,
  titleKey: PropTypes.string,
  subtitleKey: PropTypes.string,
}

DrilldownStackedChart.defaultProps = {
  onDrillDown: null,
  onDrillUp: null,
  allowUpdate: true,
  yAxisMaxValue: undefined,
  xAxisFormatter: null,
  isPerformanceComparison: false,
  titleKey: "",
  subtitleKey: "",
  onComponentUpdate: null,
}

export default translate()(DrilldownStackedChart)



// WEBPACK FOOTER //
// ./src/components/charts/generic/DrilldownStackedChart.jsx